<template>
  <BaseLayout>
    <template v-slot:top_buttons>
      <div class="demo-inline-spacing">
        <button type="button" class="btn btn-primary" @click="edit">
          Редактировать
        </button>
        <button type="button" class="btn btn-outline-secondary" @click="remove">
          Удалить
        </button>
      </div>
    </template>

    <BaseModal ref="itemModal" modal-title="Редактирование сертификата" :close-on-backdrop="false">
      <CertificateForm ref="itemForm" @success="formSuccess"/>
    </BaseModal>

    <LoadingBlock :loading="loading">
      <div v-if="certificate" class="card mb-4">
        <div class="card-body">
          <h5 class="card-title mb-4">{{ certificate.number }}</h5>
          <table class="table">
            <tbody>
            <tr>
              <td><b>Вид</b></td>
              <td>{{ getKindLabel(certificate.kind) }}</td>
            </tr>
            <tr>
              <td><b>Тип соответствия</b></td>
              <td>{{ getComplianceLabel(certificate.compliance_type) }}</td>
            </tr>
            <tr>
              <td><b>Дата выдачи</b></td>
              <td>{{ formatCertificateDate(certificate.issue_date) }}</td>
            </tr>
            <tr>
              <td><b>Срок действия</b></td>
              <td>{{ certificate.is_perpetual ? 'Бессрочный' : formatCertificateDate(certificate.valid_until) }}</td>
            </tr>
            <tr>
              <td><b>Бренды</b></td>
              <td>
                <span v-if="certificate.brands?.length">
                  <span v-for="tag in certificate.brands" :key="tag" class="badge bg-label-primary me-1">{{ tag }}</span>
                </span>
                <span v-else>—</span>
              </td>
            </tr>
            <tr>
              <td><b>Артикулы</b></td>
              <td>
                <span v-if="certificate.articles?.length">
                  <span v-for="tag in certificate.articles" :key="tag" class="badge bg-label-info me-1">{{ tag }}</span>
                </span>
                <span v-else>—</span>
              </td>
            </tr>
            <tr>
              <td><b>ТНВЭД</b></td>
              <td>
                <span v-if="certificate.tnved?.length">
                  <span v-for="tag in certificate.tnved" :key="tag" class="badge bg-label-secondary me-1">{{ tag }}</span>
                </span>
                <span v-else>—</span>
              </td>
            </tr>
            <tr v-if="certificate.registry_urls?.length">
              <td><b>Реестр</b></td>
              <td>
                <div v-for="url in certificate.registry_urls" :key="url">
                  <a :href="url" target="_blank" rel="noopener noreferrer">Открыть в реестре</a>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="certificate" class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">Документ</h5>
        </div>
        <div class="card-body">
          <div v-if="!certificate.document && certificate.registry_urls?.length">
            <p class="text-muted mb-2">Документ загружен из реестра, файла нет.</p>
            <div v-for="url in certificate.registry_urls" :key="url">
              <a :href="url" target="_blank" rel="noopener noreferrer">Открыть в реестре</a>
            </div>
          </div>
          <DocumentPreview v-else :document="certificate.document"/>
          <div v-if="certificate.document?.source_url" class="mt-2 small text-muted text-break">
            Источник:
            <a :href="certificate.document.source_url" target="_blank" rel="noopener noreferrer">
              {{ certificate.document.source_url }}
            </a>
          </div>
        </div>
      </div>

      <div v-if="certificate" class="card">
        <div class="card-header">
          <h5 class="mb-0">Привязанные товары</h5>
        </div>
        <div class="card-body">
          <ProductLinker
              :certificate-id="certificate.id"
              :linked-product-ids="certificate.product_ids || []"
              @changed="reload"
          />
        </div>
      </div>
    </LoadingBlock>
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/layouts/Base.vue'
import {PageInfo} from "@/data/page";
import {user} from "@/data/user";
import BaseModal from "@/components/Modal.vue";
import LoadingBlock from "@/components/page/LoadingBlock.vue";
import {getInfo, removeItem} from "@/api/certificate";
import CertificateForm from "@/components/forms/Certificate.vue";
import DocumentPreview from "@/components/certificate/DocumentPreview.vue";
import ProductLinker from "@/components/certificate/ProductLinker.vue";
import {
    formatCertificateDate,
    getComplianceLabel,
    getKindLabel,
} from "@/data/certificate";

export default {
  name: 'CertificateDetailView',
  components: {
    ProductLinker,
    DocumentPreview,
    CertificateForm,
    LoadingBlock,
    BaseModal,
    BaseLayout,
  },
  data() {
    return {
      certificate: null,
      loading: true,
    }
  },
  methods: {
    getKindLabel,
    getComplianceLabel,
    formatCertificateDate,
    async load() {
      this.loading = true
      const result = await getInfo(this.$route.params.id)
      this.certificate = result.data
      this.setPageInfo()
      this.loading = false
    },
    async reload() {
      await this.load()
    },
    edit() {
      setTimeout(() => {
        this.$refs.itemModal.openModal()
        this.$refs.itemForm.updateItem(this.certificate)
      }, 100)
    },
    async formSuccess() {
      this.$refs.itemModal.closeModal()
      await this.load()
    },
    async remove() {
      if (!confirm('Вы уверены, что хотите удалить сертификат?')) {
        return
      }
      await removeItem(this.certificate.id)
      await this.$router.push({name: 'certificates'})
    },
    setPageInfo() {
      PageInfo.title = this.certificate?.number || 'Сертификат'
      PageInfo.breadcrumbs = [
        {title: 'Главная', link: '/'},
        {title: 'Сертификаты', link: '/certificates'},
        {title: PageInfo.title},
      ]
    },
  },
  async mounted() {
    await user.getUserBase()
    await this.load()
  },
  watch: {
    '$route.params.id'() {
      this.load()
    },
  },
}
</script>
